import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';

import { getDaysInYearsAndMonths } from '@/features/battery/utils/DateUtils';
import { PackHistoryResponse } from '@/generated/GeneratedApiTypes';
import { BatteryHistoryGraphTooltipKpi } from '@/features/batteryHistoryGraph/BatteryHistoryGraphTooltipKpi';

type PackHistoryGraphTooltipProps = {
    active?: boolean;
    payload?: any;
    packHistory: PackHistoryResponse[];
    hoveredPackId: string | null;
    selectedPackIds: string[];
};

export const PackHistoryGraphTooltip = ({
    active,
    payload,
    packHistory,
    hoveredPackId,
    selectedPackIds,
}: PackHistoryGraphTooltipProps) => {
    const intl = useIntl();

    const effectivePackId = hoveredPackId ?? (selectedPackIds.length === 1 ? selectedPackIds[0] : null);

    if (!active || !payload?.length || !effectivePackId) {
        return null;
    }

    const timestamp = payload[0]?.payload?.timestamp as string;
    const pack = packHistory.find((p) => p.pack_id === effectivePackId);
    const entry = pack?.history?.find((e) => e.timestamp === timestamp);

    if (!entry) {
        return null;
    }

    return (
        <div
            className="border border-width-1 border-color-light bg-white padding-x-10 padding-y-10"
            data-testid="pack-history-graph-tooltip"
        >
            <div>{moment(timestamp).format('D MMMM YYYY')}</div>
            <div className="text-medium">
                <FormattedMessage id="intl-msg:batteryService.batteryPackLevel.pack" /> {effectivePackId}
            </div>
            <hr className="margin-y-5" />
            <BatteryHistoryGraphTooltipKpi
                i18nId="intl-msg:batteryService.batteryPackLevel.packSoH"
                value={entry.state_of_health_percentage}
                unit="%"
                additionalContent={
                    entry.state_of_health_status ? (
                        <span className="text-medium">
                            (
                            {intl.formatMessage({
                                id: `intl-msg:batteryService.batteryStatus.${entry.state_of_health_status.toLowerCase()}`,
                            })}
                            )
                        </span>
                    ) : undefined
                }
            />
            <BatteryHistoryGraphTooltipKpi
                i18nId="intl-msg:batteryService.batteryPackLevel.packAge"
                value={entry.usage_days}
                formatter={(value) => getDaysInYearsAndMonths(value, intl)}
            />
            <BatteryHistoryGraphTooltipKpi
                i18nId="intl-msg:batteryService.batteryPackLevel.packMileage"
                value={entry.mileage}
                unit="km"
            />
            <BatteryHistoryGraphTooltipKpi
                i18nId="intl-msg:batteryService.fullChargeEnergy"
                value={entry.full_charge_energy}
                unit="kWh"
            />
            <BatteryHistoryGraphTooltipKpi
                i18nId="intl-msg:batteryService.dischargedEnergyThroughput"
                value={entry.discharging_energy_throughput}
                unit="kWh"
            />
        </div>
    );
};
